import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { NewsCard, LoadingSpinner } from '../../components';
import './NewsPage.css';
import { list_news, count_news, get_user_info } from '../../api/functions';
import { useApi } from '../../api/api';
import { useAuth } from '../../provider/auth';

const NewsPage = () => {
  const { t, i18n } = useTranslation();
  const listNewsApi = useApi(list_news, 350);
  const countNewsApi = useApi(count_news);
  const getUserInfoApi = useApi(get_user_info);
  const { token } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(9);
  const [batchSize] = useState(36);
  const [isNewsLoading, setIsNewsLoading] = useState(true);
  const [totalNews, setTotalNews] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setIsNewsLoading(true);
      const userInfo = await getUserInfoApi.execute();
      const showPrivate = userInfo?.is_member || false;
      const language = (i18n.language === 'cs' ? 'cz' : i18n.language) || 'en';
      const countResult = await countNewsApi.execute({
        show_private: showPrivate,
        search_query: searchQuery || undefined,
        language
      });

      if (countResult && countResult.count !== undefined) {
        setTotalNews(countResult.count);
      }

      const firstItemIndex = (currentPage - 1) * itemsPerPage;
      const batchIndex = Math.floor(firstItemIndex / batchSize);
      const skip = batchIndex * batchSize;

      await listNewsApi.execute({
        skip,
        limit: batchSize,
        show_private: showPrivate,
        search_query: searchQuery || undefined,
        language
      });

      setIsNewsLoading(false);
    };
    fetchData();
  }, [token, currentPage, itemsPerPage, searchQuery, batchSize, i18n.language]);

  const allNews = listNewsApi.data || [];

  useEffect(() => {
    const updateItemsPerPage = () => {
      const width = window.innerWidth;
      if (width < 960) {
        setItemsPerPage(4);
      } else if (width < 1440) {
        setItemsPerPage(6);
      } else {
        setItemsPerPage(9);
      }
      setCurrentPage(1);
    };

    updateItemsPerPage();
    window.addEventListener('resize', updateItemsPerPage);
    return () => window.removeEventListener('resize', updateItemsPerPage);
  }, []);

  const firstItemIndex = (currentPage - 1) * itemsPerPage;
  const batchIndex = Math.floor(firstItemIndex / batchSize);
  const batchStartIndex = batchIndex * batchSize;
  const offsetInBatch = firstItemIndex - batchStartIndex;

  const displayNews = allNews.slice(offsetInBatch, offsetInBatch + itemsPerPage);
  const totalPages = Math.ceil(totalNews / itemsPerPage);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);

      let startPage = Math.max(2, currentPage - 2);
      let endPage = Math.min(totalPages - 1, currentPage + 2);

      if (startPage > 2) {
        pageNumbers.push('...');
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }

      pageNumbers.push(totalPages);
    }

    return pageNumbers;
  };

  const handlePageChange = (page) => {
    if (typeof page === 'number' && page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  return (
    <div className="news-page">
      <main className="news-page-main">
        <div className="news-page-container">
          <div className="news-page-header">
            <h1 className="news-page-title">{t('news.title')}</h1>
            <div className="news-page-search">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <circle cx="11" cy="11" r="8" strokeWidth="2" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder={t('news.searchPlaceholder') || 'Search news by title...'}
                value={searchQuery}
                onChange={handleSearchChange}
                className="news-page-search-input"
              />
              {searchQuery && (
                <button
                  className="news-page-search-clear"
                  onClick={() => setSearchQuery('')}
                  aria-label="Clear search"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {searchQuery && (
            <div className="news-page-results">
              {allNews.length === 0 ? (
                <p>{t('news.noResults') || 'No news found matching your search'}</p>
              ) : (<></>)
              }
            </div>
          )}

          {isNewsLoading ? (
            <LoadingSpinner message={t('news.loading') || 'Loading news...'} />
          ) : displayNews.length > 0 ? (
            <>
              <div className="news-page-grid">
                {displayNews.map((news, index) => (
                  <div key={news.id} className="fade-in-stagger">
                    <NewsCard news={news} />
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="news-page-pagination">
                  <button
                    className="news-page-pagination-btn news-page-pagination-prev"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  <div className="news-page-pagination-numbers">
                    {getPageNumbers().map((pageNum, index) => (
                      pageNum === '...' ? (
                        <span key={`ellipsis-${index}`} className="news-page-pagination-ellipsis">
                          ...
                        </span>
                      ) : (
                        <button
                          key={pageNum}
                          className={`news-page-pagination-number ${pageNum === currentPage ? 'active' : ''}`}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      )
                    ))}
                  </div>

                  <button
                    className="news-page-pagination-btn news-page-pagination-next"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="news-page-empty">
              <img src="/svg/document.svg" alt="" />
              <p>{t('news.noNewsAvailable') || 'No news available'}</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default NewsPage;
